import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Button,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { MaterialIcon } from '../ui/MaterialIcon';

interface MergeConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  noteCount: number;
  survivingNoteTitle: string;
  attachmentCount: number;
  isMerging?: boolean;
}

const MergeConfirmDialog: React.FC<MergeConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  noteCount,
  survivingNoteTitle,
  attachmentCount,
  isMerging = false,
}) => {
  // AlertDialog needs `leastDestructiveRef` for focus; point it at the close
  // X (project pattern: dismiss via top-right close, no Cancel button).
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const bgColor = 'bg.surface';
  const textColor = 'text.primary';
  const subtextColor = 'text.secondary';
  const warningBg = 'bg.warning';
  const warningBorder = 'border.warning';

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={closeButtonRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent bg={bgColor} color={textColor} maxW="480px">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <VStack spacing={3} align="center">
              <MaterialIcon name="merge" color="brand.onSurface" boxSize={8} />
              <Text>Merge {noteCount} notes</Text>
            </VStack>
          </AlertDialogHeader>

          <AlertDialogCloseButton ref={closeButtonRef} isDisabled={isMerging} />

          <AlertDialogBody>
            <VStack spacing={4} align="stretch">
              <Text textAlign="center">
                The content of {noteCount} notes will be combined into{' '}
                <Text as="span" fontWeight="bold">
                  {survivingNoteTitle || 'Untitled'}
                </Text>{' '}
                in chronological order.
                {attachmentCount > 0 &&
                  ` ${attachmentCount} attachment${attachmentCount !== 1 ? 's' : ''} will be moved to the merged note.`}{' '}
                Source notes will be permanently deleted.
              </Text>

              <Box
                fontSize="sm"
                color={subtextColor}
                bg={warningBg}
                p={3}
                borderRadius="md"
                border="1px"
                borderColor={warningBorder}
              >
                <Text textAlign="center">
                  This action cannot be undone. The merged note will keep the original timestamp of
                  the oldest note.
                </Text>
              </Box>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              bg="brand.primary"
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={onConfirm}
              isLoading={isMerging}
              loadingText="Merging..."
            >
              Merge notes
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export { MergeConfirmDialog };
